/**
 * App Badging API wrapper.
 *
 * Affiche un badge numérique sur l'icône du dock / taskbar quand la PWA est installée.
 * Fallback silencieux si l'API n'est pas supportée (Safari pré-16.4, navigateurs sans
 * support PWA, ou page ouverte en onglet et non installée).
 *
 * Spec: https://developer.mozilla.org/en-US/docs/Web/API/Badging_API
 */

type NavigatorWithBadging = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function isBadgingSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as NavigatorWithBadging;
  return typeof nav.setAppBadge === 'function' && typeof nav.clearAppBadge === 'function';
}

/**
 * Set the badge to `count`. If count is 0 or negative, clears the badge instead.
 * Caps the displayed number at 99 (the OS already does this on macOS).
 */
export async function setBadge(count: number): Promise<void> {
  if (!isBadgingSupported()) return;
  const nav = navigator as NavigatorWithBadging;
  try {
    if (count <= 0) {
      await nav.clearAppBadge?.();
      return;
    }
    const safe = Math.min(Math.floor(count), 9999);
    await nav.setAppBadge?.(safe);
  } catch {
    // L'API peut throw si l'app n'est pas installée. On reste silencieux.
  }
}

export async function clearBadge(): Promise<void> {
  if (!isBadgingSupported()) return;
  const nav = navigator as NavigatorWithBadging;
  try {
    await nav.clearAppBadge?.();
  } catch {
    // ignore
  }
}
